import { useLicensors } from "@with-nx/ecommerce";
import { Ecommerce } from "@with-nx/ecommerce";
import {
  Formatter,
  ProductionPhotoHelper,
  useCMSBlogs,
  useCMSFeatureds,
  useCMSPartners,
  useCMSRandomProductionPhoto,
  useLicensorImage,
} from "@with-nx/hooks-n-helpers";
import { useServerRegion } from "@with-nx/region";
import {
  DesignedButton,
  ReusableSection,
  Rule,
} from "@with-nx/simple-ui/atoms";
import { VisibleContainer } from "@with-nx/simple-ui/molecules";
import {
  AutomaticGetStarted,
  CatalogLayout,
  GradientHolder,
  GridBlog,
  GridStepScroller,
  ImageGallery,
  ReusableDoubleGradientCard,
  ReusableSlantedContainer,
  SolutionsHero,
  SolutionsSearch,
} from "@with-nx/simple-ui/organisms";
import { HomeCommercialTemplate } from "@with-nx/simple-ui/templates";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Box } from "simple-effing-primitive-layout";

import ReusableSearchSection from "../../components/w/ReusableSearchSection";

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
export const getServerSideProps = async ({ resolvedUrl }) => {
  return {
    props: {
      resolvedUrl,
    },
  };
};
export default function Page({ resolvedUrl }) {
  const router = useRouter();
  const { licensors } = useLicensors();
  const [licensor, _licensor] =
    useState<Ecommerce.Schemas.LicensorSchemaType>();
  const [all, _all] = useState(false);

  useEffect(() => {
    if (!licensors?.length) {
      return;
    }

    const l = licensors.find((i) => {
      if (
        slugify(i?.name || "") === router?.query?.slug ||
        slugify(i?.abbreviation || "") === router?.query?.slug
      ) {
        return true;
      }
      return false;
    });

    _licensor(l);
  }, [licensors, router?.query?.slug]);

  const { images } = useCMSRandomProductionPhoto(
    licensor?.name
      ? {
          filter: {
            licensorTags: [licensor.name],
          },
        }
      : {},
    Boolean(licensor?.name)
  );

  const partners = useCMSPartners({});
  const partner = partners?.partners?.find(
    (i) => i.slug === router?.query?.slug
  );

  const isSponsor = ["aact", "edta"].includes(partner?.slug);

  const region = useServerRegion();
  const featureds = useCMSFeatureds({});
  const _featureds = featureds.featureds.filter((i) => {
    return (
      i?.partnerTags?.includes(partner?._id) &&
      i?.regionTags?.includes(region?.id?.toString() || "")
    );
  });

  const licensorImage = useLicensorImage(
    licensor?.name,
    licensor?.abbreviation
  );

  const blogsFilter = isSponsor
    ? { per: 4 }
    : {
        licensorTags: [licensor?.name],
      };

  const blogs = useCMSBlogs(
    licensor?.name
      ? {
          filter: blogsFilter,
        }
      : {},
    Boolean(licensor?.name || isSponsor)
  );

  useEffect(() => {
    if (!partners.loading && !partner?._id) {
      router.replace("/404");
    }
  }, [partner?._id, partners.loading]);

  const displaySearchSection =
    partner?.partnerTypeTags.includes("Licensor") && licensor;

  if (!partner) {
    return null;
  }

  return (
    <CatalogLayout
      footerNoAngle
      transparentFixedNavBar
      title={partner?.name ? `Broadway Media - ${partner.name}` : undefined}
      description={partner?.description}
      resolvedUrl={resolvedUrl}
    >
      <SolutionsHero
        title={partner.name}
        description={partner.description}
        buttons={["request-information", "schedule-a-call"]}
        src="/assets/hero/partner-background.webp"
        slanted={false}
        sponsors={isSponsor ? [partner.slug] : null}
        right={
          !isSponsor ? (
            <Box parse="d:flex j:flex-end a:center h:100% ">
              <img
                loading="lazy"
                alt={licensor?.name}
                style={{
                  height: "auto",
                  width: 256,
                  cursor: "pointer",
                }}
                src={Formatter.image(licensorImage?.image)}
              />
            </Box>
          ) : undefined
        }
      />

      {_featureds.length > 0 ? (
        <ReusableSlantedContainer>
          <div className="container">
            <Rule parse="!hl d:block mb:36">Featured Show Collections</Rule>
            <GridStepScroller
              xs={24}
              sm={24}
              md={8}
              items={[
                ..._featureds.map((i, ii) => {
                  if (!all && ii >= 3) {
                    return null;
                  }

                  if (all && ii >= 9) {
                    return null;
                  }

                  return (
                    <GradientHolder
                      src={Formatter.image(i.image, true)}
                      key={i._id}
                      render={(colors) => (
                        <ReusableDoubleGradientCard
                          key={i._id}
                          gradients={
                            colors.ready
                              ? [colors.hex, colors.hex]
                              : ["transparent", "transparent"]
                          }
                          title={i.name}
                          holder={false}
                          press={() => {
                            router.push(i.url);
                          }}
                          properties={{ height: 256 }}
                        >
                          <Box parse="p:absolute t:0 l:0 w:100% h:100% d:flex a:center j:center">
                            {colors.ready ? (
                              <img
                                loading="lazy"
                                width="176"
                                height="176"
                                src={colors.src}
                                alt={i.name || "Broadway Media"}
                              />
                            ) : undefined}
                          </Box>
                        </ReusableDoubleGradientCard>
                      )}
                    />
                  );
                }),
              ]}
            />
            {all ? undefined : (
              <Box parse="d:flex j:flex-end mt:24">
                <DesignedButton
                  size="small"
                  theme="tetriary"
                  label="View All Shows"
                  press={() => {
                    _all(true);
                  }}
                />
              </Box>
            )}
          </div>
        </ReusableSlantedContainer>
      ) : undefined}

      {images?.length ? (
        <ReusableSection>
          <ImageGallery
            per={3}
            images={images.map((image) => {
              return {
                src: Formatter.image(image.image),
                meta: {
                  copyright: image.copyrightClaim,
                  organization: image.organizationName,
                  name: image.name,
                  date: image.productionDate,
                },
                pop: false,
                press: () => {
                  ProductionPhotoHelper.redirect(image);
                },
              };
            })}
            autoScroll
          />
        </ReusableSection>
      ) : undefined}

      {blogs.blogs.length > 0 ? (
        <ReusableSection>
          <div className="container">
            <Rule rule="hl" color="var(--font1)" bottom={24} display="block">
              {partner?.name}{" "}
              <span style={{ color: "var(--primary)" }}>Stories</span>
            </Rule>
            <GridStepScroller
              xs={24}
              sm={24}
              md={12}
              lg={8}
              xl={8}
              items={blogs.blogs.map((blog, b) => {
                if (b > 2) {
                  return null;
                }

                return (
                  <GridBlog
                    key={blog._id}
                    date={blog.date}
                    description={blog.description}
                    image={Formatter.compressed(blog.coverImage, 256, true)}
                    title={blog.title}
                    url={`/blog/${blog.slug}`}
                    transparent={false}
                    properties={{
                      height: "100%",
                    }}
                  />
                );
              })}
            />
          </div>
        </ReusableSection>
      ) : undefined}

      {displaySearchSection ? (
        <VisibleContainer>
          <SolutionsSearch
            title={`Explore ${licensor.name || licensor.abbreviation} Shows`}
          >
            <div id="shows" />
            <ReusableSearchSection
              licensor={licensor?.id || undefined}
              productCategory="global"
            />
          </SolutionsSearch>
        </VisibleContainer>
      ) : undefined}

      {isSponsor && (
        <ReusableSection>
          <div className="container">
            <HomeCommercialTemplate />
            <AutomaticGetStarted />
          </div>
        </ReusableSection>
      )}
    </CatalogLayout>
  );
}
